// Chalo mode (docs/plans/2026-10-17-chalo.md): three police lines round Jantar Mantar, crowds in tolis, gates that open
// and re-close. Geometry comes from data/maps/<map>.rings.json (tools/cordons.py: the lines laid on the streets,
// docs/plans/2026-10-18-chokepoints.md), rules from data/modes/chalo.json.
// Pure like sim.ts: the server and the predicting client build the same masks from the same gap bits.
import { GameMap, Vec, buildOctileField } from './map';

// ------------------------------------------------------------------ data files

type XY = [number, number];
/** naka: concrete blocks across a carriageway; fence: steel crowd fence on a pavement or a lawn */
export interface CordonRun { kind: 'naka' | 'fence'; tiles: XY[]; along: number }
export interface RingsJson {
  tileM: number;
  centre: { x: number; y: number };
  van: { x: number; y: number };
  site: { tiles: number; areaM2: number };
  /** a ring is a police line: its cordon tiles, and the same tiles in rows of one kind, in order along the line */
  rings: { name: string; cordon: XY[]; runs: CordonRun[]; checkpoints: string[] }[];
  /** on: a gap across a carriageway or a gate on the pavement; along: the line's direction there (screen degrees, y down) */
  checkpoints: { id: string; ring: string; name: string; mission: string; x: number; y: number; gapTiles: XY[]; on: 'road' | 'pavement'; along: number; facing: number; toStageM: number }[];
  links: Record<string, Record<string, number>>;
  entries: { name: string; kind: string; x: number; y: number; weight: number; closesAtS: number | null; toOuterGateM: Record<string, number> }[];
  bus: { x: number; y: number };
  march: { x: number; y: number; lengthM: number; path: XY[] };
  checks: { sealed: boolean; eachGapJoins: boolean };
}

type Text = { en: string; hi: string };
export interface Mission { name: Text; playersMin: number; playersMax: number; durationS: number; limitS: number; success: number }
/** Dharna, the sit-in at an inner gate (chalo.json missions.dharna.play) */
export interface DharnaPlay { sitWithinM: number; toliWithinM: number; calmPerSitter: number; calmPerToli: number; breakBelow: number; breakAfterS: number }
/** Baat, talking to the inspector (chalo.json missions.baat.play) */
export interface BaatPlay {
  talkWithinM: number; exchanges: number; pickS: number; moodStart: number; moodWin: number; hinted: number; calm: number; heat: number;
  jogWithinM: number; jogCost: number; retryS: number; openS: number; recloseSpeed: number;
}
export interface BaatScript { hints: Record<CalmKind, Text[]>; lines: Record<LineKind, Text[]>; won: Text; lost: Text }
export interface TeamRow {
  players: [number, number]; goal: number; ringFalls: Record<string, number>; openS: Record<string, number>; holdS: number; successShift: number; expectedWin: [number, number];
}
export interface ChaloRules {
  mode: 'chalo';
  players: { min: number; max: number; speedMps: number; jogMps: number; detainedS: number };
  round: { introS: number; gatherS: number; marchS: number };
  teamSize: TeamRow[];
  crowd: {
    toliSize: number; arrivalPeoplePerS: number; stopArrivalsAtS: number; speedMps: number; passTolisPerS: number;
    rerouteIfOpenGateWithinM: number; waitBeforeLeavingS: number; leaveOnePersonEveryS: number;
  };
  /** holding the site (docs/plans/2026-10-18-crowd-as-structure.md): what frays a toli's hausla and what steadies it */
  hold?: {
    nearM: number; edgeLopsided: number; crushWithinM: number; crushAbove: number; cordonWithinM: number;
    drainPerS: { base: number; edge: number; cordon: number; crush: number };
    steadyM: number; steadyPerS: number; driftBelow: number; driftSpeedFactor: number; leaveBelow: number;
  };
  director: { timeline: { atS: number; event: string; entry?: string; recloseFactor?: number; forS?: number; say?: Text }[] };
  missions: Record<string, Mission> & { dharna: Mission & { play: DharnaPlay }; baat: Mission & { play: BaatPlay; script: BaatScript } };
  checklist: { id: string; kind: string; text: Text; items?: string[]; count?: number }[];
}
export interface ChaloData { rings: RingsJson; rules: ChaloRules }

/** The rules for a team of n players (the smallest row below 3 players). */
export function teamRow(R: ChaloRules, n: number): TeamRow {
  return R.teamSize.find((r) => n >= r.players[0] && n <= r.players[1]) ?? (n < R.teamSize[0].players[0] ? R.teamSize[0] : R.teamSize[R.teamSize.length - 1]);
}

// ------------------------------------------------------------------ geometry

export const LEVEL_SITE = 3; // levels: 0 outside the outer ring, 1 between outer and middle, 2 between middle and inner, 3 the site

export interface Checkpoint { id: string; name: string; ring: number; mission: string; gaps: number[]; at: Vec; aside: Vec }
export interface ChaloGeo {
  ringNames: string[];
  cps: Checkpoint[];
  /** per tile: 1 on a cordon tile of any ring (solid for the crowd and protesters, always) */
  cordon: Uint8Array;
  /** per tile: the checkpoint index + 1 whose gap it is, else 0 */
  gapOf: Uint8Array;
  /** per tile: how many rings it is inside (cordon and gap tiles count as outside their own ring) */
  level: Uint8Array;
  /** per checkpoint: octile field to its gap, within the band in front of its ring; then one to the stage, within the site */
  fields: Float32Array[];
  stageField: Float32Array;
  entries: (Vec & { name: string; weight: number; closesAtS: number | null })[];
  centre: Vec; van: Vec; bus: Vec;
}

const px = (m: GameMap, x: number, y: number): Vec => ({ x: x * m.tile, y: y * m.tile });
const tidx = (m: GameMap, t: XY) => t[1] * m.w + t[0];

/** Tiles 4-connected to `from` through tiles where open(i). */
function flood(m: GameMap, from: number, open: (i: number) => boolean): Uint8Array {
  const seen = new Uint8Array(m.w * m.h), q = [from];
  if (!open(from)) return seen;
  seen[from] = 1;
  for (let h = 0; h < q.length; h++) {
    const i = q[h], x = i % m.w;
    for (const j of [x > 0 ? i - 1 : -1, x < m.w - 1 ? i + 1 : -1, i - m.w, i + m.w]) if (j >= 0 && j < seen.length && !seen[j] && open(j)) { seen[j] = 1; q.push(j); }
  }
  return seen;
}

/** Built once per map and data (cached on the map, like its flow fields). */
export function chaloGeo(m: GameMap, D: ChaloData): ChaloGeo {
  const c = m as GameMap & { _chalo?: ChaloGeo };
  if (c._chalo) return c._chalo;
  const R = D.rings, n = m.w * m.h;
  const ringNames = R.rings.map((r) => r.name);
  const cordon = new Uint8Array(n), gapOf = new Uint8Array(n), ringBand: Uint8Array[] = R.rings.map(() => new Uint8Array(n));
  R.rings.forEach((r, ri) => { for (const t of r.cordon) { cordon[tidx(m, t)] = 1; ringBand[ri][tidx(m, t)] = 1; } });
  R.checkpoints.forEach((cp, k) => { const ri = ringNames.indexOf(cp.ring); for (const t of cp.gapTiles) { gapOf[tidx(m, t)] = k + 1; ringBand[ri][tidx(m, t)] = 1; } });

  const centre = Math.floor(R.centre.y) * m.w + Math.floor(R.centre.x);
  const level = new Uint8Array(n);
  for (const band of ringBand) {
    const inside = flood(m, centre, (i) => !m.solid[i] && !band[i]);
    for (let i = 0; i < n; i++) level[i] += inside[i];
  }

  const cps: Checkpoint[] = R.checkpoints.map((cp) => {
    const ring = ringNames.indexOf(cp.ring), gaps = cp.gapTiles.map((t) => tidx(m, t));
    // where the squad waits while the gate is open: just behind its line (the walkable ground the line closes off),
    // at least 2 tiles from every gap so it never stands in one; else the nearest cordon tile of its ring 2 tiles off
    const gx = cp.x, gy = cp.y, allGaps = R.checkpoints.flatMap((c) => c.gapTiles);
    const clear = (tx: number, ty: number) => allGaps.every((g) => Math.max(Math.abs(g[0] - tx), Math.abs(g[1] - ty)) >= 2);
    let best: XY = cp.gapTiles[0], bd = Infinity;
    for (let i = 0; i < n; i++) {
      const tx = i % m.w, ty = (i / m.w) | 0;
      if (level[i] !== ring + 1 || m.solid[i] || cordon[i] || gapOf[i] || !clear(tx, ty)) continue;
      const d = Math.hypot(tx + 0.5 - gx, ty + 0.5 - gy);
      if (d < bd) { bd = d; best = [tx, ty]; }
    }
    if (bd > 6) { // no room behind the line close by: along it, as before
      bd = Infinity;
      for (const t of R.rings[ring].cordon) {
        const dmin = Math.min(...cp.gapTiles.map((g) => Math.max(Math.abs(g[0] - t[0]), Math.abs(g[1] - t[1]))));
        const d = Math.hypot(t[0] + 0.5 - gx, t[1] + 0.5 - gy);
        if (dmin >= 2 && d < bd) { bd = d; best = t; }
      }
    }
    return { id: cp.id, name: cp.name, ring, mission: cp.mission, gaps, at: px(m, cp.x, cp.y), aside: px(m, best[0] + 0.5, best[1] + 0.5) };
  });

  // a gate's field covers the band in front of its ring, its ring's gaps and the gaps of the ring before (so a toli
  // crossing in is already on it)
  const fields = cps.map((cp) => {
    const within = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      const g = gapOf[i] - 1, gr = g >= 0 ? cps[g].ring : -1;
      within[i] = (level[i] === cp.ring && !cordon[i] && g < 0) || gr === cp.ring || gr === cp.ring - 1 ? 1 : 0;
    }
    return buildOctileField(m, cp.gaps, within);
  });
  const site = new Uint8Array(n);
  const inner = cps.filter((cp) => cp.ring === ringNames.length - 1);
  for (let i = 0; i < n; i++) site[i] = (level[i] === LEVEL_SITE && !cordon[i] && !gapOf[i]) || inner.some((cp) => cp.gaps.includes(i)) ? 1 : 0;
  const stageField = buildOctileField(m, [centre], site);

  const g: ChaloGeo = {
    ringNames, cps, cordon, gapOf, level, fields, stageField,
    entries: R.entries.map((e) => ({ ...px(m, e.x, e.y), name: e.name, weight: e.weight, closesAtS: e.closesAtS })),
    centre: px(m, R.centre.x, R.centre.y), van: px(m, R.van.x, R.van.y), bus: px(m, R.bus.x, R.bus.y),
  };
  c._chalo = g;
  return g;
}

/**
 * The play area for protesters and tolis with the gates in `bits` open (bit k = checkpoint k): 1 = walkable. Cordon
 * tiles are always solid; a gap tile is solid while its gate is closed. Cached per bit pattern.
 */
export function chaloMask(m: GameMap, G: ChaloGeo, bits: number): Uint8Array {
  const c = G as ChaloGeo & { _masks?: Map<number, Uint8Array> };
  const hit = c._masks?.get(bits);
  if (hit) return hit;
  const k = new Uint8Array(m.w * m.h);
  for (let i = 0; i < k.length; i++) {
    const g = G.gapOf[i] - 1;
    k[i] = G.cordon[i] ? 0 : g >= 0 ? (bits >> g) & 1 : 1;
  }
  (c._masks ??= new Map()).set(bits, k);
  return k;
}

/**
 * Where a detained player is let go (chalo.json players.respawn): outside the first ring still standing, nearest the
 * checkpoint they were taken at. With the outer ring up that is an arrival point (the open entries, else any); once
 * rings have fallen it is the walkable ground just outside the ring in front, so nobody walks rings that are down.
 */
export function releasePoint(m: GameMap, G: ChaloGeo, from: Vec, front: number, open: ChaloGeo['entries']): Vec {
  const d = (p: Vec) => Math.hypot(p.x - from.x, p.y - from.y);
  if (front <= 0) {
    const list = open.length ? open : G.entries;
    return list.reduce((a, b) => (d(b) < d(a) ? b : a));
  }
  let best = -1, bd = Infinity;
  for (let i = 0; i < m.w * m.h; i++) {
    if (G.level[i] !== front || m.solid[i] || G.cordon[i] || G.gapOf[i]) continue;
    const p = { x: ((i % m.w) + 0.5) * m.tile, y: (((i / m.w) | 0) + 0.5) * m.tile }, di = d(p);
    if (di < bd) { bd = di; best = i; }
  }
  return best < 0 ? G.entries[0] : { x: ((best % m.w) + 0.5) * m.tile, y: (((best / m.w) | 0) + 0.5) * m.tile };
}

// ------------------------------------------------------------------ gate state (shared by sim, snapshot and HUD)

/** closed: police in the gap; game: a game is running (later steps); open: until `until`; back: police walking back */
export type GateState = 'closed' | 'game' | 'open' | 'back';
export const GATE_CODE: Record<GateState, number> = { closed: 0, game: 1, open: 2, back: 3 };
export const CODE_GATE: GateState[] = ['closed', 'game', 'open', 'back'];

/**
 * `cool`: no new game before this (seconds into the gather); `slow`: police walk back at Baat's re-close speed;
 * `sitIn`: opened by a sit-in, so it shuts when the sit-in ends
 */
export interface Gate { state: GateState; until: number; opened: boolean; credit: number; byTalk: boolean; cool: number; slow: boolean; sitIn: boolean }

// ------------------------------------------------------------------ the games of build step 2: Dharna and Baat

/** The kinds of line in Baat. The first three are calm; the inspector's words hint at one of them. */
export const LINE_KINDS = ['facts', 'rights', 'people', 'heat'] as const;
export type LineKind = typeof LINE_KINDS[number];
export type CalmKind = Exclude<LineKind, 'heat'>;

/** Baat's mood change for a line of `kind` (null: no pick in time) when the inspector hinted at `hint`, and whether someone jogged close by. */
export function baatDelta(P: BaatPlay, kind: LineKind | null, hint: CalmKind, jogged: boolean): number {
  const d = kind === null ? 0 : kind === hint ? P.hinted : kind === 'heat' ? P.heat : P.calm;
  return d - (jogged ? P.jogCost : 0);
}

/** Dharna's calm: sitters (marshals would count 1.5 once roles exist) plus the tolis queued close by. */
export const dharnaCalm = (P: DharnaPlay, sitters: number, tolis: number) => sitters * P.calmPerSitter + tolis * P.calmPerToli;

/** Players a game needs: games for 2 or more need only 1 in a team of 3 or fewer (plan section 3, small teams). */
export const playersNeeded = (min: number, humans: number) => (humans <= 3 ? Math.min(1, min) : min);

/**
 * A talk in progress, as the snapshot carries it: [gate, talker id, exchange (0-based), mood, tenths of a second
 * left to pick, hint kind, hint line, then 3 options as (kind, line) pairs, the last exchange's mood change].
 * Kinds index LINE_KINDS; lines index the script's lists.
 */
export type TalkRow = number[];
/** A sit-in in progress: [gate, sitters, calm x10, pressure x10, tenths held, tenths below the break line, tenths needed]. */
export type SitRow = number[];

/** Snapshot block for the mode. */
export interface ChaloSnap {
  /** gap bits: bit k set while checkpoint k is walkable */
  g: number;
  /** per checkpoint: [state code, seconds left open (-1: held open for good), ever opened 0/1, seconds until a game may start again, opened by talk 0/1] */
  cs: number[][];
  /** talks and sit-ins under way */
  tk?: TalkRow[]; dh?: SitRow[];
  /** fallen rings, as bits; the ring in front (the first standing one) */
  f: number; r: number;
  /** [people at the site, goal, people arrived, people who went home, players detained this round] */
  c: number[];
  /** round result once over: 1 won, 0 lost, -1 still playing */
  w: number;
  /** seconds into the round (the director's clock: data/modes/chalo.json director.timeline atS), to 0.1 s */
  t: number;
}
